/**
 * Avatar d'un enfant — la représentation par défaut, et le choix qu'on laisse aux parents.
 *
 * Trois silhouettes, pas deux. Une alternative « fille / garçon » obligerait chaque parent
 * à répondre à une question que le dossier ne pose nulle part ailleurs : Repère
 * n'enregistre pas le genre d'un enfant, parce qu'il n'en a pas l'usage. `neutre` est donc
 * le défaut, et les deux autres sont là pour les parents qui les préfèrent — un enfant qui
 * se reconnaît dans son avatar rend l'application plus familière, et c'est le seul but.
 *
 * Aucune silhouette n'est déduite du prénom : un prénom ne dit pas le genre d'un enfant,
 * et se tromper sur celui-ci, sur l'écran que ses deux parents consultent tous les jours,
 * serait une petite violence répétée.
 */
export const SILHOUETTES_AVATAR = ['neutre', 'fille', 'garcon'];
/**
 * Les couleurs proposées, nommées d'après la palette d'avatars de `@repere/ui-tokens`.
 *
 * Une liste fermée plutôt qu'un sélecteur libre : chaque paire fond/trait y est **mesurée**
 * en contraste, et un parent qui choisirait un jaune pâle rendrait la silhouette invisible.
 *
 * Elle est distincte de la palette de l'interface, et c'est voulu : celle-ci est faite pour
 * du chrome — sobre, beaucoup de bleu — et posée sur l'avatar d'un enfant de six ans elle
 * donne un écran d'administration. Le rouge du drapeau reste écarté ; le rose et le violet
 * n'ont pas ce problème, personne n'a jamais signalé une alerte en rose.
 */
export const COULEURS_AVATAR = [
    'navy',
    'turquoise',
    'sage',
    'amber',
    'rose',
    'violet',
    'corail',
    'muted',
];
export const LIBELLES_SILHOUETTES = {
    neutre: 'Neutre',
    fille: 'Fille',
    garcon: 'Garçon',
};
export const LIBELLES_COULEURS = {
    navy: 'Bleu',
    turquoise: 'Turquoise',
    sage: 'Vert doux',
    amber: 'Ambre',
    rose: 'Rose',
    violet: 'Violet',
    corail: 'Corail',
    muted: 'Gris',
};
export function estSilhouetteAvatar(valeur) {
    return SILHOUETTES_AVATAR.includes(valeur);
}
export function estCouleurAvatar(valeur) {
    return COULEURS_AVATAR.includes(valeur);
}
/**
 * Âge en années révolues.
 *
 * Calculé à l'affichage, jamais stocké : un âge en base est faux dès le lendemain de
 * l'anniversaire, et le rattraper demanderait une tâche planifiée là où une soustraction
 * suffit.
 *
 * Le jour de l'anniversaire compte pour l'année nouvelle — c'est ainsi qu'un enfant
 * l'entend, et l'écran qu'il voit ce jour-là doit le dire.
 */
export function ageEnAnnees(naissance, a) {
    let annees = a.getUTCFullYear() - naissance.getUTCFullYear();
    const mois = a.getUTCMonth() - naissance.getUTCMonth();
    if (mois < 0 || (mois === 0 && a.getUTCDate() < naissance.getUTCDate()))
        annees -= 1;
    return Math.max(annees, 0);
}
/**
 * L'avatar d'un enfant qui n'en a pas encore choisi.
 *
 * Un défaut partagé plutôt qu'un `null` : sans lui, chaque écran refait le même choix de
 * repli et ils finissent par ne plus s'accorder — l'accueil dessinerait une silhouette
 * bleue, la fiche une grise, et le parent croirait à un bug.
 */
export const AVATAR_PAR_DEFAUT = {
    silhouette: 'neutre',
    color: 'navy',
};
//# sourceMappingURL=avatar.js.map